import "./osd.scss";

import classNames from "classnames";
import { Point } from "openseadragon";
import { useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";

import { ViewerMode } from "custom-models/ViewerTypes";

import {
  changeViewerMode,
  fetchAnnotations,
  slideBarcodeUrl,
  slideMacroUrl,
  toggleAnnotationVisibility,
} from "shared/actions/slideDetailActions";
import {
  slideRangeManifest,
  tilesLoaded,
} from "shared/actions/slideTileActions";
import { useHasLoaded } from "shared/hooks/requestStatusHooks";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { cleanUrl } from "shared/utils/openseadragon/bookmark_url";

import { ReactComponent as HomeIcon } from "icons/home-solid.svg";
import { ReactComponent as MinusIcon } from "icons/minus.svg";
import { ReactComponent as PlusIcon } from "icons/plus.svg";
import { ReactComponent as RotateRight10Icon } from "icons/rotate10.svg";
import { ReactComponent as RotateRight90Icon } from "icons/rotate90.svg";
import { useAppSelector } from "store/hooks";
import { AppDispatch } from "store/store";

import { DialogEnum } from "./dialogs/Dialog";
import OSDClient, { ZOOMS } from "./OSDClient";
import OSDClientEffectiveMagnification from "./OSDClientEffectiveMagnification";
import ZoomSpan from "./ZoomSpan";

interface OSDProps {
  slideDetailObject: SlideDetailState;
  setDialog: Function;
  dialog: DialogEnum | null;
  manuallyClosedDialog?: boolean;
}

const DEFAULT_ZOOM = 0.5;

const OSD = (props: OSDProps) => {
  const { dialog, manuallyClosedDialog, setDialog, slideDetailObject } = props;

  const aiOverlayFlag = useAppSelector((state) =>
    selectFeatureFlag(state, "aiOverlay")
  );
  const effectiveMagnificationFlag = useAppSelector((state) =>
    selectFeatureFlag(state, "effectiveMagnification")
  );
  const closeSpecimenOnZoomFlag = useAppSelector(
    (state) => !selectFeatureFlag(state, "dontCloseSpecimenOnZoom")
  );

  const hasRangeManifestLoaded = useHasLoaded(slideRangeManifest, {
    id: slideDetailObject.id,
  });

  const { viewerMode, slide_rotation_degrees = 0 } = slideDetailObject;

  const range_manifest = useAppSelector(
    ({ slideTiles }) => slideTiles.range_manifest
  );

  const viewerRef = useRef<OSDClientEffectiveMagnification | OSDClient | null>(
    null
  );

  const viewerDiv = useRef<HTMLDivElement>(null);
  const prevZoom = useRef<number | undefined>(undefined);

  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
  const [rotate, setRotate] = useState<number>(
    slideDetailObject.slide_rotation_degrees
      ? slideDetailObject.slide_rotation_degrees
      : 0.0
  );
  const [closeSpecimenOnZoom, setCloseSpecimenOnZoom] = useState(true);

  const dispatch = useDispatch<AppDispatch>();

  const handleZoomIn = () => {
    viewerRef.current?.doZoomIn();
  };

  const handleZoomOut = () => {
    viewerRef.current?.doZoomOut();
  };

  const handleRotate = (angle: number) => {
    viewerRef.current?.doRotate(angle);
  };

  const handleHome = () => {
    console.log("er");
    viewerRef.current?.doHome();
  };

  const absoluteRotation = (r: number) => r % 360;

  useEffect(() => {
    if (closeSpecimenOnZoomFlag) {
      const zoomUnchanged = prevZoom.current === zoom;
      const roundedZoom = Math.round(zoom * 100) / 100;

      // Close the specimen dialog when zooming in >= 1x
      // Bring it back if zooming out < 1x
      if (closeSpecimenOnZoom) {
        if (dialog === DialogEnum.Specimen && roundedZoom >= 1) {
          if (zoomUnchanged) {
            setCloseSpecimenOnZoom(false);
          } else {
            setDialog(null);
          }
        } else if (
          dialog === null &&
          roundedZoom < 1 &&
          !!prevZoom.current &&
          ZOOMS.includes(roundedZoom) &&
          !zoomUnchanged &&
          !manuallyClosedDialog
        ) {
          setDialog(DialogEnum.Specimen);
        }
      }

      prevZoom.current = zoom;
    }
  }, [
    closeSpecimenOnZoom,
    closeSpecimenOnZoomFlag,
    dialog,
    manuallyClosedDialog,
    zoom,
  ]);

  useHotkeys(
    "r,z",
    () => {
      handleRotate(-10);
    },
    { scopes: ["viewerHotkeys"] },
    []
  );
  useHotkeys(
    "shift+r,c",
    () => {
      handleRotate(-90);
    },
    { scopes: ["viewerHotkeys"] },
    []
  );
  useHotkeys(
    "t,x",
    () => {
      handleRotate(10);
    },
    { scopes: ["viewerHotkeys"] },
    []
  );
  useHotkeys(
    "shift+t,v",
    () => {
      handleRotate(90);
    },
    { scopes: ["viewerHotkeys"] },
    []
  );
  useHotkeys(
    "=",
    () => {
      handleZoomIn();
    },
    { scopes: ["viewerHotkeys"] },
    []
  );
  useHotkeys(
    "minus",
    () => {
      handleZoomOut();
    },
    { scopes: ["viewerHotkeys"] },
    []
  );

  useHotkeys(
    "Shift+8",
    () => {
      if (viewerMode === ViewerMode.PAN_ZOOM) {
        dispatch(toggleAnnotationVisibility());
      }
    },
    { scopes: ["viewerHotkeys"] },
    [viewerMode]
  );

  useHotkeys("Escape", () => {
    dispatch(changeViewerMode(ViewerMode.PAN_ZOOM));
  });

  useHotkeys("up", () => {
    const viewport = viewerRef.current?.viewer?.viewport;
    viewport?.panBy(viewport?.deltaPointsFromPixels(new Point(0, -40)));
  });

  useHotkeys("down", () => {
    const viewport = viewerRef.current?.viewer?.viewport;
    viewport?.panBy(viewport?.deltaPointsFromPixels(new Point(0, 40)));
  });

  useHotkeys("left", () => {
    const viewport = viewerRef.current?.viewer?.viewport;
    viewport?.panBy(viewport?.deltaPointsFromPixels(new Point(-40, 0)));
  });

  useHotkeys("right", () => {
    const viewport = viewerRef.current?.viewer?.viewport;
    viewport?.panBy(viewport?.deltaPointsFromPixels(new Point(40, 0)));
  });

  const shouldLoadRangeBasedPrecompute =
    slideDetailObject.full_precompute_location;
  const rangeBasedPrecomputeReady = Boolean(
    hasRangeManifestLoaded && range_manifest
  );

  const {
    objectiveDefault,
    effectiveDefault,
    assumeDefault,
    calculateEffectiveDefault,
  } = (slideDetailObject?.lab?.settings?.effective_magnification?.details ||
    {}) as {
    objectiveDefault?: number;
    effectiveDefault?: number;
    assumeDefault?: number;
    calculateEffectiveDefault?: boolean;
  };

  const calculatedEffectiveDefault =
    calculateEffectiveDefault &&
    range_manifest?.ObjectiveMagnification &&
    slideDetailObject.mpp
      ? range_manifest.ObjectiveMagnification /
        (1e6 / slideDetailObject.mpp) /
        2
      : undefined;

  const effectiveMagnification =
    objectiveDefault &&
    (calculatedEffectiveDefault || effectiveDefault) &&
    range_manifest?.ObjectiveMagnification === objectiveDefault &&
    range_manifest?.EffectiveMagnification === objectiveDefault
      ? calculatedEffectiveDefault || effectiveDefault
      : range_manifest?.EffectiveMagnification || assumeDefault;

  console.log(effectiveMagnification);

  const shouldZoomWithEffectiveMagnification =
    rangeBasedPrecomputeReady &&
    effectiveMagnificationFlag &&
    slideDetailObject?.lab?.settings?.effective_magnification?.active &&
    effectiveMagnification;

  useEffect(() => {
    if (shouldLoadRangeBasedPrecompute && !rangeBasedPrecomputeReady) return;

    if (slideDetailObject.id) {
      // Always destroy the previous "OSDClient" before creating a new one...
      viewerRef.current?.destroy();
      viewerRef.current = null;
      console.debug("Instantiating new OSDClient");
      viewerRef.current = shouldZoomWithEffectiveMagnification
        ? new OSDClientEffectiveMagnification(
            slideDetailObject.id,
            slideDetailObject.high_power_dzi_url || "",
            slideDetailObject.low_power_tile_url,
            slideDetailObject.low_power_dzi,

            slideDetailObject.full_precompute_location,
            range_manifest,
            effectiveMagnification,
            slideDetailObject.mpp,
            slide_rotation_degrees,
            (mode: ViewerMode) => {
              dispatch(changeViewerMode(mode));
            },
            (loaded: boolean) => dispatch(tilesLoaded(loaded)),
            setZoom,
            setRotate,
            aiOverlayFlag,
            slideDetailObject.lab?.settings?.landscape_rotation_fix?.active
          )
        : new OSDClient(
            slideDetailObject.id,
            slideDetailObject.high_power_dzi_url || "",
            slideDetailObject.low_power_tile_url,
            slideDetailObject.low_power_dzi,

            slideDetailObject.full_precompute_location,
            range_manifest,

            slideDetailObject.mpp,
            slide_rotation_degrees,
            (mode: ViewerMode) => {
              dispatch(changeViewerMode(mode));
            },
            (loaded: boolean) => dispatch(tilesLoaded(loaded)),
            setZoom,
            setRotate,
            aiOverlayFlag,
            slideDetailObject.lab?.settings?.landscape_rotation_fix?.active
          );

      dispatch(slideBarcodeUrl(slideDetailObject.id));
      dispatch(slideMacroUrl(slideDetailObject.id));
      dispatch(fetchAnnotations(slideDetailObject.id));
      dispatch(changeViewerMode(ViewerMode.PAN_ZOOM));
    } else {
      cleanUrl();
      viewerRef.current?.destroy();
      viewerRef.current = null;
    }
    return () => {
      if (viewerRef.current) cleanUrl();
      viewerRef.current?.destroy();
      viewerRef.current = null;
    };
  }, [
    slideDetailObject.id,
    slideDetailObject.mpp,
    slide_rotation_degrees,
    slideDetailObject.high_power_dzi_url,
    slideDetailObject.low_power_tile_url,
    slideDetailObject.low_power_dzi,
    slideDetailObject.full_precompute_location,
    hasRangeManifestLoaded,
    range_manifest,
    aiOverlayFlag,
    shouldLoadRangeBasedPrecompute,
    rangeBasedPrecomputeReady,
    shouldZoomWithEffectiveMagnification,
    effectiveMagnification,
  ]);

  useEffect(() => {
    if (slideDetailObject.full_precompute_location && slideDetailObject.id)
      // TODO: it would be a perf improvement not to "waterfall"
      // this `full_precompute_location`, and instead fetch it in
      // parallel with `/api/slides/{id}`. To do that, we need
      // ... `full_precompute_location` on the slides[] within
      // ... the `Case` back from `/api/cases/{id}
      dispatch(
        slideRangeManifest({
          url: slideDetailObject.full_precompute_location,
          id: slideDetailObject.id,
        })
      );
  }, [slideDetailObject.full_precompute_location, slideDetailObject.id]);

  useEffect(() => {
    viewerRef.current?.setViewerMode(viewerMode);
  }, [viewerMode]);

  return (
    <div
      className={classNames(
        "OSD",
        zoom <= 1 * DEFAULT_ZOOM ? "Barcode--on" : "Barcode--off"
      )}
    >
      <nav className="ControlButtons">
        {<span>{absoluteRotation(rotate)}°</span>}
        <button
          title="Rotate 90° left (Shift-R)"
          onClick={() => handleRotate(-90)}
        >
          <RotateRight90Icon className="svg-icon flipped" />
        </button>
        <button title="Rotate 10° left (R)" onClick={() => handleRotate(-10)}>
          <RotateRight10Icon className="svg-icon flipped" />
        </button>
        <button title="Rotate 10° right (T)" onClick={() => handleRotate(10)}>
          <RotateRight10Icon className="svg-icon" />
        </button>
        <button
          title="Rotate 90° right (Shift-T)"
          onClick={() => handleRotate(90)}
        >
          <RotateRight90Icon className="svg-icon" />
        </button>
        <button
          onClick={handleHome}
          className="ControlButtons__home"
          title="Reset Rotate and Zoom"
        >
          <HomeIcon className="svg-icon" />
        </button>
        <button onClick={handleZoomOut} title="Zoom Out (-)">
          <MinusIcon className="svg-icon" />
        </button>
        <button
          id="osd-control-zoom-in"
          onClick={handleZoomIn}
          title="Zoom In (+)"
        >
          <PlusIcon className="svg-icon" />
        </button>
        <ZoomSpan zoomValue={zoom} />
      </nav>
      <div
        id="viewer"
        ref={viewerDiv}
        style={{
          height: "100vh",
          width: "100vw",
          cursor:
            viewerMode !== ViewerMode.PAN_ZOOM
              ? `url(/icons/${ViewerMode[viewerMode]}.png) ${
                  viewerMode === ViewerMode.AREA_COUNT
                    ? "10 10"
                    : viewerMode === ViewerMode.TEXT
                    ? "0 0"
                    : viewerMode === ViewerMode.ROI
                    ? "5 5"
                    : "0 18"
                }, auto`
              : undefined,
        }}
        className={`Viewer Viewer--${ViewerMode[viewerMode]}`}
      />
    </div>
  );
};

export default OSD;
